import {
  type ButtonHTMLAttributes,
  type ReactNode,
  createContext,
  useContext,
} from 'react';

import Image from '../atoms/Image';
import Tooltip from '../atoms/Tooltip';
import Text from '../base/Text';
import useMediaQuery from '../hooks/useMediaQuery';
import ToggleButton from '../input/ToggleButton';

interface OverlayButtonStackRootProps {
  children?: ReactNode;
  variant?: 'simple' | 'full' | 'responsive';
  tooltipSide?: 'left' | 'right';
  inModal?: boolean;
  title?: string;
  titleClassName?: string;
  className?: string;
}

interface OverlayButtonStackButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  tooltipText: string;
  text?: ReactNode | string;
  icon?: ReactNode;
  active?: boolean;
}

interface OverlayButtonStackPreviewButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  tooltipText: string;
  text: ReactNode | string;
  icon: ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  fallback?: ReactNode;
  active?: boolean;
  previewClassName?: string;
}

interface OverlayButtonStackDividerProps {
  className?: string;
}

const OverlayButtonStackContext = createContext<{
  tooltipSide: 'left' | 'right';
  inModal: boolean;
  showText: boolean;
}>({
  tooltipSide: 'right',
  inModal: false,
  showText: true,
});

function Root({
  children,
  variant = 'simple',
  tooltipSide = 'right',
  inModal = false,
  title,
  titleClassName,
  className = '',
}: OverlayButtonStackRootProps) {
  const isLg = useMediaQuery('(min-width: 1024px)');
  const resolvedVariant =
    variant === 'responsive' ? (isLg ? 'full' : 'simple') : variant;
  const showText = resolvedVariant === 'full';

  return (
    <OverlayButtonStackContext.Provider
      value={{ tooltipSide, inModal, showText }}
    >
      <div
        className={`dark:bg-gray-90/40 bg-gray-15/45 pointer-events-auto flex w-min flex-col gap-2 rounded-xl p-1.5 backdrop-blur-md ${resolvedVariant === 'full' ? 'items-stretch' : 'items-end lg:items-stretch'} ${className}`}
      >
        {resolvedVariant === 'full' && title && (
          <Text
            variant="studio/body/medium/xs"
            className={`hidden flex-none items-end pt-1 text-center lg:block ${titleClassName ?? ''}`.trim()}
          >
            {title}
          </Text>
        )}
        {children}
      </div>
    </OverlayButtonStackContext.Provider>
  );
}

function Button({
  tooltipText,
  text,
  icon,
  active = false,
  disabled = false,
  ...props
}: OverlayButtonStackButtonProps) {
  const { tooltipSide, inModal, showText } = useContext(
    OverlayButtonStackContext,
  );
  const isLg = useMediaQuery('(min-width: 1024px)');
  const resolvedText = showText ? text : undefined;

  return (
    <Tooltip
      color="default"
      text={tooltipText}
      side={tooltipSide}
      inModal={inModal}
      disabled={Boolean(resolvedText) && isLg}
    >
      <ToggleButton
        active={active}
        text={resolvedText}
        icon={icon}
        disabled={disabled}
        {...props}
      />
    </Tooltip>
  );
}

function PreviewButton({
  tooltipText,
  text,
  icon,
  imageSrc,
  imageAlt = '',
  fallback,
  active = false,
  disabled = false,
  className = '',
  previewClassName = '',
  ...props
}: OverlayButtonStackPreviewButtonProps) {
  const { tooltipSide, inModal } = useContext(OverlayButtonStackContext);
  const isLg = useMediaQuery('(min-width: 1024px)');

  return (
    <Tooltip
      color="default"
      text={tooltipText}
      side={tooltipSide}
      inModal={inModal}
      disabled={isLg}
    >
      <button
        type="button"
        className={`flex w-full flex-col gap-1 rounded-lg p-0.5 backdrop-blur-md transition-colors lg:p-1.5 ${
          active
            ? 'dark:bg-gray-90 bg-white'
            : 'dark:bg-gray-90/45 bg-gray-15/25 dark:hover:bg-gray-90 hover:bg-white'
        } ${className}`.trim()}
        disabled={disabled}
        {...props}
      >
        <div
          className={`pointer-events-auto h-[55px] w-[55px] overflow-hidden rounded-md transition-colors duration-100 lg:aspect-square lg:h-[103px] lg:w-full ${previewClassName}`.trim()}
        >
          {imageSrc ? (
            <Image
              className="h-full w-full object-cover"
              src={imageSrc}
              alt={imageAlt}
            />
          ) : (
            <div className="bg-secondary flex h-full w-full items-center justify-center rounded-md">
              {fallback}
            </div>
          )}
        </div>
        <div className="hidden items-center justify-start gap-1 lg:flex">
          {icon}
          {typeof text === 'string' ? (
            <Text variant="studio/body/medium/xs">{text}</Text>
          ) : (
            text
          )}
        </div>
      </button>
    </Tooltip>
  );
}

function Divider({ className = '' }: OverlayButtonStackDividerProps) {
  return (
    <hr
      className={`hidden h-px w-full border-0 bg-white/50 lg:block ${className}`.trim()}
    />
  );
}

export default {
  Root,
  Button,
  PreviewButton,
  Divider,
};
